import DeleteBeneficiaryContact from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/Commands/DeleteBeneficiaryContact';
import deleteBeneficiaryContact from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/deleteBeneficiaryContact';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import contactToDeleteSignal, {
	closeContactToDelete,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryContacts/Signals/contactToDeleteSignal';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { ConfirmDeleteDialog } from '@verclam/ui-kit';

/**
 * The single confirmation mounted by `BeneficiaryContacts` (V-14): hard deletion of a contact. The
 * signal only ever carries the id of the contact to confirm; the contact itself is read straight from
 * `beneficiaryContactsStore.contacts`, already in cache from the owning screen's own fetch.
 */
const BeneficiaryContactsConfirmationDialogs: React.FC = () => {
	const dispatch = useDispatch();
	const params = useParams<{ beneficiary: string }>();
	const beneficiaryId = params.beneficiary !== undefined ? Number(params.beneficiary) : Number.NaN;
	const { currentFacility } = useStrictCurrentFacility();

	const contacts = useSelector((state) => state.beneficiaryContactsStore.contacts);
	const contactToDeleteId = useSignalValue(contactToDeleteSignal);
	const contactToDelete = contacts?.findById(contactToDeleteId) ?? null;

	const handleDialogHide = React.useCallback(() => {
		closeContactToDelete();
	}, []);

	const handleAccept = React.useCallback(() => {
		if (contactToDelete === null || Number.isNaN(beneficiaryId)) {
			return;
		}

		dispatch(
			deleteBeneficiaryContact(
				new DeleteBeneficiaryContact(currentFacility.id, beneficiaryId, contactToDelete.id),
			),
		).catch(ignoreHandledError);
		// `acceptCallback` never triggers `onHide` on `ConfirmDeleteDialog`: close explicitly, immediately,
		// rather than waiting for the request to settle and leaving the dialog open long enough for a
		// second click.
		closeContactToDelete();
	}, [contactToDelete, currentFacility, beneficiaryId, dispatch]);

	const message = contactToDelete?.isPersonOfTrust
		? "La suppression est définitive. Ce dossier n'aura plus de personne de confiance désignée."
		: 'La suppression est définitive.';

	return (
		<ConfirmDeleteDialog
			visible={contactToDelete !== null}
			onHide={handleDialogHide}
			header="Supprimer ce contact"
			message={message}
			confirmText={contactToDelete?.fullName ?? ''}
			acceptCallback={handleAccept}
		/>
	);
};

export default BeneficiaryContactsConfirmationDialogs;
